/**
 * 고객 리포트 — 질문 하나가 한 **행**이다. 잉크가 닿는 것은 넷뿐이다:
 * 질문 문구 · 브랜드명 · 언급률 수치 · 추이 그래프 (260921-client-report-chart-only).
 *
 * ★ 카드·테두리·그림자·필터 상자·설명 문단을 걷어냈다. 행을 가르는 것은 1px 선 하나와 여백이다.
 * ★ **이 화면에서 색을 가진 것은 곡선 하나뿐이다**
 *   (dataviz: 텍스트는 데이터 색을 입지 않는다 — 그 원칙을 UI 크롬까지 넓혔다).
 * ★ **기간은 화면 맨 위의 회차 하나다.** 측정 영역에 있던 7/14/30/90 세그먼트를 없앴다
 *   (2026-10-01) — 한 화면에 기간이 둘이면 위의 산출물과 아래의 그래프가 **다른 기간을 말하는데
 *   그 사실이 화면 어디에도 안 적힌다.** 서버도 `days` 와 `cycle` 을 동시에 받으면 400 을 낸다.
 * ★ 히어로 숫자는 **질문이 하나일 때만** 크게 간다(52px). 행마다 그걸 세우면 "뷰당 히어로 하나" 가 깨지고
 *   열둘이 서로 소리를 지른다.
 * ★ 운영자 화면(`/admin`)은 overview.tsx 의 표를 그대로 쓴다.
 */
import type { Rate } from '@geo-admin/shared/contract/common.ts';
import { metrics, registry, works } from '@geo-admin/shared/contract/api/index.ts';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect } from 'react';
import { BrandPicker } from '../components/BrandPicker.tsx';
import { CyclePicker } from '../components/CyclePicker.tsx';
import { Deliverables } from '../components/Deliverables.tsx';
import { TrendArt } from '../components/TrendArt.tsx';
import { ChartIcon } from '../components/icons.tsx';
import { Empty, ErrorBox } from '../components/ui/states.tsx';
import { useApi, useApiList } from '../lib/api.ts';

import { customerRate, deltaText } from '../lib/format.ts';
import { useAppBrand } from '../lib/appBrand.ts';
import { useCycleBack } from '../lib/works.ts';
import { cn } from '../lib/utils.ts';

/**
 * 수치. ★ 고객에게는 **표본에 관한 정보를 보이지 않는다** — 표본이 얇아도 비율 그대로다
 * (`customerRate`). 운영자 화면은 `RateCell` 이 k/n·95% CI·표본 부족을 계속 보인다.
 * ★ 증감의 기준은 **직전 회차**다 — "같은 길이의 앞 구간"이 아니다(회차는 28~31일로 길이가 다르다).
 *   `cycleWindow` 의 `prevFrom`/`prevTo` 가 그 구간이고, 화면은 그 사실을 문구로만 말한다.
 */
function Figure({ rate, previous, big }: { rate: Rate; previous: Rate; big: boolean }) {
  const d = deltaText(rate, previous);
  return (
    <div className="leading-none">
      {/* 큰 독립 숫자는 proportional — tabular 는 모든 자리를 0 폭으로 벌려 헐거워 보인다 */}
      <div className={cn('proportional-nums font-semibold text-slate-900', big ? 'text-[52px]' : 'text-[30px]')}>
        {customerRate(rate)}
      </div>
      {/* 증감 자리는 **늘 있다** — 비교할 자료가 없는 행만 값이 빈다. 행마다 높이가 달라지지 않게 */}
      <div className="mt-2 text-slate-500 text-xs tabular-nums">
        {d ? `${d.mark ? `${d.mark} ` : ''}${d.label}` : '—'} · 직전 회차 대비
      </div>
    </div>
  );
}

export function ReportPage() {
  // **주소가 브랜드를 정한다**(`/app/report?brand=…`, 운영자 결정 2026-10-13 · lib/appBrand.ts) — 공유한 주소가 같은 브랜드
  //   보고서를 연다. 브랜드가 여럿이면(261013-account-brand-membership — 옛 대행사 포함) 머리의 선택기로 바꾼다
  const navigate = useNavigate();
  const { brand: wanted } = useSearch({ strict: false }) as { brand?: string };
  const appBrand = useAppBrand(wanted);
  const pickBrand = (id: string, replace = false) =>
    void navigate({ to: '/app/report', search: { brand: id }, replace });
  // 주소에 브랜드가 없거나 내 브랜드가 아니면 정해진 브랜드로 **주소를 고친다** — 지금 보이는 화면의 주소가 곧 공유 주소다
  const settled = appBrand.current?.brandId;
  useEffect(() => {
    if (settled && settled !== wanted) void navigate({ to: '/app/report', search: { brand: settled }, replace: true });
  }, [settled, wanted, navigate]);
  // ★ 고객도 부른다(`scoped` 라 **자기 브랜드**만 온다) — 제목에 이름을 넣기 위해서다.
  //   `Me` 에는 `brandId` 만 있고 이름이 없다. 계약을 넓히는 대신 이미 있는 범위 조회를 쓴다
  const brands = useApi(registry.listBrands, {});
  // ★ 이 리포트는 브랜드 하나 관점이다(261001-client-works-report ⑤) — 회차는 그 브랜드의 보고 기간이라
  //   브랜드가 특정되지 않으면 서버가 400 을 낸다. "전체"가 없는 이유다
  const list = (brands.data?.brands ?? []).filter((c) => c.status === 'active');
  const brandId = appBrand.current?.status === 'active' ? appBrand.current.brandId : '';
  const ready = brandId !== '';
  const current = brandId ? list.find((c) => c.id === brandId) : undefined;
  // 고른 브랜드가 결제 대기면 보고서가 없다 — 구독을 시작하라고 안내한다
  const awaitingPayment = appBrand.current?.status === 'confirmed';
  // 처음 여는 회차는 서버가 정한다 — 어제가 든 회차(구독일 당일이면 막 끝난 회차, 261020-report-cycle-yesterday-basis)
  const cyc = useCycleBack(brandId);
  const back = cyc.back;
  const setBack = cyc.setBack;
  const worksQ = useApi(
    works.getWorks,
    { query: { cycle: cyc.param, brandId: brandId || undefined } },
    { enabled: ready },
  );
  useEffect(() => cyc.learn(worksQ.data?.openBack), [worksQ.data?.openBack, cyc.learn]);
  // ★ 지표는 회차를 안 뒤에 묻는다 — 구독일 당일에 빈 새 회차를 한 번 묻고 버리지 않게
  const overview = useApi(
    metrics.overview,
    { query: { cycle: back, brandId: brandId || undefined } },
    { enabled: ready && cyc.ready },
  );
  const rows = overview.data?.rows ?? [];
  // ★ 행마다 같은 회차를 넘긴다 — 헤드라인과 곡선이 다른 기간이면 한 행 안에서 어긋난다
  const trends = useApiList(
    metrics.queryTrend,
    rows.map((r) => ({ params: { id: r.queryId }, query: { cycle: back } })),
  );
  const single = rows.length === 1;
  // 측정이 시작된 날 — 그 전의 빈 날은 그리지 않는다. 한 값을 모든 행에 똑같이 넘겨 축을 맞춘다
  const firstMeasured = trends
    .flatMap((t) => t?.data?.daily ?? [])
    .filter((d) => d.mention.n > 0)
    .map((d) => d.date)
    .sort()[0];

  return (
    <>
      {/* ★ 순서: 산출물이 먼저, 인용률 그래프가 아래다 (운영자 결정 2026-10-01).
          측정 영역의 **마크업은 그대로**이고 자리만 옮겼다 — h1 이 둘이 되지 않게 제목만 h2 로 내렸다 */}
      <header className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-slate-200 border-b pb-4">
        <div className="flex items-center gap-4">
          {/* ★ 제목이 **누구의 리포트인지**를 말한다 — 그래서 응답의 줄마다 `brandName` 을 싣지 않는다
              (261001-client-works-report). 이름이 아직 없으면 제목만 보인다 — 빈 자리가 생기지 않게 */}
          {/* ★ 고객명이 주어이고 나머지는 **그 문서가 무엇인가**를 말하는 꼬리다 — 굵기와 농도로 가른다.
              꼬리는 `slate-600` 이다. 실측으로 이 바탕에서 `slate-500` 은 **3.83:1**, `slate-400` 은
              더 낮아 둘 다 AA(4.5:1)에 못 미친다 — 20px·weight 500 은 WCAG 의 "큰 글자"가 아니다
              (≥24px 이거나 ≥18.66px + bold 여야 한다). 이 레포가 겪은 "글자가 배경에 묻힘"과 같은 자리다 */}
          <h1 className="text-xl">
            {current ? <span className="font-bold text-slate-900">{current.name} </span> : null}
            <span className="font-medium text-slate-600">AI검색 최적화 보고서</span>
          </h1>
          {/* ★ 고객 셀렉터는 **페이지 전체**를 가른다 — 측정 섹션 안에 두면 그 섹션만 바뀌는 것처럼 읽힌다.
              회차 선택과 나란히 두는 것이 맞다. 둘 다 "무엇에 대한 리포트인가"를 정한다 */}
          {/* ★ 고객 고르기는 버튼 → 선택 창이다(운영자 결정 2026-10-11). 맨 select 는 고객이 많으면 찾기 어려웠다.
              "전체"가 없다 — 아래 `ready` 주석 참조 */}
          {appBrand.brands.length > 1 && appBrand.current ? (
            <BrandPicker
              items={appBrand.brands.map((b) => ({
                id: b.brandId,
                name: b.status === 'confirmed' ? `${b.brandName} (결제 대기)` : b.brandName,
                brandNames: [],
              }))}
              value={appBrand.current.brandId}
              onChange={(id) => pickBrand(id)}
              noun="브랜드"
            />
          ) : null}
        </div>
        <CyclePicker cycle={worksQ.data?.cycle} back={back} onBack={setBack} />
      </header>
      {awaitingPayment ? (
        <div className="grid place-items-center gap-3 rounded-xl border border-slate-200 border-dashed py-16 text-center">
          <p className="text-slate-600 text-sm">{appBrand.current?.brandName} 의 구독을 시작하면 보고서가 열립니다</p>
          <Link to="/app/billing" className="font-medium text-live hover:underline">
            구독 관리로 가기
          </Link>
        </div>
      ) : !ready && !appBrand.loading && !appBrand.current ? (
        // 보고서를 볼 브랜드가 아직 없다 — 대행사 직원은 브랜드 0개·승인 대기로도 이 화면에 온다(261025-agency-white-label)
        <div className="grid place-items-center gap-3 rounded-xl border border-slate-200 border-dashed py-16 text-center">
          <p className="font-semibold text-slate-800">보고서를 볼 브랜드가 아직 없습니다</p>
          <p className="text-slate-600 text-sm">
            {appBrand.pending.length > 0
              ? `${appBrand.pending.map((b) => b.brandName).join(', ')} — 승인되면 측정을 시작하고 보고서가 열립니다`
              : '고객 브랜드를 추가하면 승인 뒤 측정을 시작합니다'}
          </p>
          <Link to="/app/brands" className="font-medium text-live hover:underline">
            브랜드 {appBrand.pending.length > 0 ? '보기' : '추가하기'}
          </Link>
        </div>
      ) : !ready ? (
        <div className="h-64" />
      ) : worksQ.error ? (
        <ErrorBox error={worksQ.error} />
      ) : worksQ.data ? (
        <Deliverables data={worksQ.data} limit={12} />
      ) : (
        // 자리를 잡아 둔다 — 글자 한 줄로 줄었다가 늘어나며 출렁이지 않게
        <div className="h-64" />
      )}

      {/* ★ 산출물 네 섹션과 **같은 위계**다 (2026-10-01) — `Deliverables` 의 `Section` 머리를 그대로 맞췄다.
          측정은 다섯째 섹션이지 다른 장(章)이 아니다. 아이콘 색은 **아래 곡선과 같은 `--color-brand`** 라,
          이 섹션에서 색을 가진 것이 여전히 "그 데이터 하나"다.
          ★ **기준일(`9/29 기준`) 표기를 뺐다** — 기간은 맨 위 회차 하나로 말한다 */}
      {ready ? (
        <section className="border-slate-200 border-t pt-5">
          <h2 className="mb-2 flex items-center gap-2 font-bold text-lg text-slate-900">
            <ChartIcon className="h-[1.1em] w-[1.1em] shrink-0 text-[var(--color-brand)]" />
            ChatGPT 노출
          </h2>
          {/* ★ 산출물 네 섹션과 **같은 자리에 같은 꼴**로 한 줄을 둔다 — 다섯 중 하나만 없으면
            위계가 다시 어긋난다. ★ 표본에 관한 말을 쓰지 않는다(건수·주기·`k/n`) —
            고객 화면의 금지 범위다 (260921-client-report-chart-only) */}
          <p className="mb-3 text-[13px] text-slate-500">AI 검색 답변에 고객 브랜드가 얼마나 나타나는지 측정합니다</p>
          {!overview.data ? (
            overview.error ? (
              <ErrorBox error={overview.error} />
            ) : (
              // 첫 로딩에도 자리를 잡아 둔다 — 글자 한 줄로 줄었다가 늘어나며 출렁이지 않게
              <div className="h-64" />
            )
          ) : rows.length === 0 ? (
            <Empty>아직 등록된 질문이 없습니다</Empty>
          ) : (
            // 갱신 중에는 눌러만 둔다. 기간을 바꿀 때 목록이 통째로 사라지지 않는다
            <div className={cn('transition-opacity duration-200', overview.isFetching && 'opacity-40')}>
              {rows.map((r, i) => {
                const t = trends[i];
                return (
                  <div
                    key={r.queryId}
                    className="grid animate-rise items-start gap-x-10 gap-y-3 border-slate-200 border-b py-7 last:border-0 lg:grid-cols-[264px_1fr]"
                    style={{ animationDelay: `${Math.min(i, 7) * 40}ms` }}
                  >
                    <div>
                      <p className="font-medium text-[15px] text-slate-900">{r.query}</p>
                      <p className="mt-1 text-[13px] text-slate-500">{r.brandNames.join(' · ')}</p>
                      <div className="mt-6">
                        <p className="mb-2 text-slate-400 text-xs">브랜드 언급률</p>
                        <Figure rate={r.mention} previous={r.previous} big={single} />
                      </div>
                    </div>
                    {t?.error ? (
                      <ErrorBox error={t.error} />
                    ) : (
                      // 아직 안 온 그래프는 **빈칸으로 자리를 지킨다** — 레이아웃이 뛰지 않는다
                      <div style={{ minHeight: single ? 200 : 148 }}>
                        {t?.data ? (
                          <TrendArt
                            daily={t.data.daily}
                            markers={t.data.markers}
                            from={firstMeasured}
                            height={single ? 200 : 148}
                          />
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : null}
    </>
  );
}
